import './membership-list.component.scss';

import type { FunctionComponent } from 'preact';
import { Fragment } from 'preact';
import { memo } from 'preact/compat';
import { useCallback } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { useTranslation } from '../../../i18n';
import type { AccountSettings } from '../../../models/common.model';
import type { AppDispatch } from '../../../store';
import type { Contract, Membership } from '../../models/membership.model';
import { updatePaymentMethod } from '../../store/membership.thunk';
import {
  getMembershipLabel,
  isContractFailed,
  isContractFreeTier,
  isTierCurrent,
} from '../../utils/membership.utlis';
import { MembershipShippingBilling } from '../membership-shipping-billing/membership-shipping-billing.component';
import { MembershipTier } from '../membership-tier/membership-tier.component';

interface MembershipListProps {
  memberships: Membership[];
  accountSettings: AccountSettings;
  showProgramName?: boolean;
  showTierImage?: boolean;
  showTierName?: boolean;
  showTierDescription?: boolean;
  showPrice?: boolean;
  showQuickAdd?: boolean;
  showViewPerks?: boolean;
  redirectToCurrentUrl?: boolean;
}

const MembershipListComponent: FunctionComponent<MembershipListProps> = ({
  memberships,
  accountSettings,
  showProgramName,
  showTierImage,
  showTierName,
  showTierDescription,
  showPrice,
  showQuickAdd,
  showViewPerks = true,
  redirectToCurrentUrl = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  // Memoized event handler
  const handlePaymentUpdateClick = useCallback(
    (contract: Contract) => {
      dispatch(updatePaymentMethod(contract));
    },
    [dispatch]
  );

  // Early return for empty state
  if (!memberships || memberships.length === 0) {
    return (
      <div className="single-card-grid single-membership-list">
        <div>{t('membership.noMembershipsAvailable')}</div>
      </div>
    );
  }

  return (
    <div className="single-card-grid single-membership-list">
      {memberships.map((membership) => {
        // Filter out null/undefined memberships
        if (!membership) {
          return null;
        }
        const membershipLabel = getMembershipLabel(membership);
        return (
          <Fragment key={membership.id}>
            {/* Failed Payment Banner */}
            {isContractFailed(membership.contract) && (
              <button
                type="button"
                className="failed-tier"
                onClick={() => handlePaymentUpdateClick(membership.contract)}
              >
                {t('membership.transactionFailed', { label: membershipLabel })}
              </button>
            )}
            {/* Membership Tiers Grid */}
            <div className="single-card single-card-product single-fan-subscription">
              {membership.tiers?.map((tier) => (
                <div
                  key={tier.id}
                  className={`single-card-option ${isTierCurrent(tier) ? 'current' : ''}`}
                >
                  <MembershipTier
                    membership={membership}
                    tier={tier}
                    accountSettings={accountSettings}
                    showProgramName={showProgramName}
                    showTierImage={showTierImage}
                    showTierName={showTierName}
                    showTierDescription={showTierDescription}
                    showPrice={showPrice}
                    showQuickAdd={showQuickAdd}
                    showViewPerks={showViewPerks}
                    redirectToCurrentUrl={redirectToCurrentUrl}
                  />
                </div>
              ))}
            </div>
            {/* Shipping/Billing (only for paid contracts) */}
            {membership.contract && !isContractFreeTier(membership.contract) && (
              <MembershipShippingBilling membership={membership} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export const MembershipList = memo(MembershipListComponent);
